import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getSpecializedAgentKey } from '../services/specialized-agent';
import {
  approveAgentUnshareRequest,
  createAgentUnshareRequest,
  getAgentUnshareRequest,
  getUsedInWorkflows,
  rejectAgentUnshareRequest,
} from '../services/unifiedAgents';

export function useAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  enabled = true
) {
  const { key, queryFn } = getAgentUnshareRequest(instanceId, agentType, agentId);
  const { data } = useQuery({
    queryKey: key,
    queryFn,
    enabled: enabled && !!instanceId && !!agentId,
  });

  return { pendingUnshareRequest: data?.pending_unshare_request ?? null };
}

export function useCreateAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string
) {
  const queryClient = useQueryClient();
  const unshareRequestKey = getAgentUnshareRequest(instanceId, agentType, agentId).key;

  return useMutation({
    mutationFn: () => createAgentUnshareRequest(instanceId, agentType, agentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: unshareRequestKey });
      toaster.create('Request sent', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to send unshare request', { type: 'danger' });
    },
  });
}

export function useResolveAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string
) {
  const queryClient = useQueryClient();
  const unshareRequestKey = getAgentUnshareRequest(instanceId, agentType, agentId).key;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: unshareRequestKey });
    void queryClient.invalidateQueries({ queryKey: getSpecializedAgentKey(agentId) });
    void queryClient.invalidateQueries({ queryKey: ['agents'] });
  };

  const approve = useMutation({
    mutationFn: (requestId: string) =>
      approveAgentUnshareRequest(instanceId, agentType, agentId, requestId),
    onSuccess: () => {
      invalidate();
      toaster.create('Agent removed from your organization', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to approve unshare request', { type: 'danger' });
    },
  });

  const reject = useMutation({
    mutationFn: (requestId: string) =>
      rejectAgentUnshareRequest(instanceId, agentType, agentId, requestId),
    onSuccess: () => {
      invalidate();
      toaster.create('Unshare request declined', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to decline unshare request', { type: 'danger' });
    },
  });

  return { approve, reject };
}

export function useAgentUsedInWorkflows(instanceId: string, agentId: string, enabled = true) {
  const { key, queryFn } = getUsedInWorkflows(instanceId, agentId);
  const query = useQuery({
    queryKey: key,
    queryFn,
    enabled: enabled && !!instanceId && !!agentId,
  });

  return {
    workflows: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
